import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KlubSiteList from '../../components/klub/KlubSiteList';
import { AppFilterBar, AppFilterInput, AppFilterSelect } from '../../components/filters';
import { AppAlert, AppButton } from '../../components/ui';
import klubService from '../../services/klubService';
import { ROUTES } from '../../config/routes';
import { goBackOr } from '../../utils/historyBack';
import { FORMULE_LABELS, MATCH_STATUS_LABELS } from '../../utils/klubUtils';
import {
  LIFECYCLE_PHASES,
  PHASE_ACTIFS,
  PHASE_INTEGRATION,
  PHASE_RESILIATION,
  isChecklistStep,
  isStepDone,
  phaseForStep,
  phaseFromStatut,
  stepFilterLabel,
} from '../../utils/klubPipelinePhases';
import { singleOrEmpty } from '../../utils/kkpPlanningFilters';
import '../../styles/klub-fiche.scss';
import '../../styles/kkp-planning-page.scss';

const paramList = (value) => (value ? [value] : []);

const MONTH_OPTIONS = [
  { value: '1', label: 'Janvier' },
  { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' },
  { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' },
  { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const KlubListingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [formule, setFormule] = useState([]);
  const [matchStatus, setMatchStatus] = useState(
    () => paramList(searchParams.get('dolibarr_match_status')),
  );
  const [currentWorkflowStepCode, setCurrentWorkflowStepCode] = useState(
    () => paramList(searchParams.get('current_workflow_step_code')),
  );
  const [lifecyclePhase] = useState(
    () => paramList(searchParams.get('lifecycle_phase')),
  );
  const [fromDolibarr] = useState(searchParams.get('from_dolibarr') === '1' ? ['1'] : []);
  const [renouvellement60j] = useState(
    searchParams.get('renouvellement_60j') === '1' ? ['1'] : [],
  );
  const [preavis] = useState(
    () => paramList(searchParams.get('preavis')),
  );
  const [mois, setMois] = useState(() => paramList(searchParams.get('mois')));
  const [pipelineTemplate, setPipelineTemplate] = useState(null);
  const [catalogReady, setCatalogReady] = useState(false);

  const selectedPhase = singleOrEmpty(lifecyclePhase);
  const isActifsView = selectedPhase === PHASE_ACTIFS;
  const isResiliationView = selectedPhase === PHASE_RESILIATION;
  const dashboardBackUrl = selectedPhase
    ? `${ROUTES.klub.dashboard}?vue=${selectedPhase}`
    : ROUTES.klub.dashboard;
  const listQuery = searchParams.toString();
  const listReturnTo = listQuery
    ? `${ROUTES.klub.adherents}?${listQuery}`
    : ROUTES.klub.adherents;

  const pipelineSteps = useMemo(
    () => [...(pipelineTemplate?.steps ?? [])].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0)),
    [pipelineTemplate],
  );

  const stepsByPhase = useMemo(() => {
    const buckets = Object.fromEntries(LIFECYCLE_PHASES.map((item) => [item.code, []]));
    pipelineSteps.forEach((step) => {
      if (!isChecklistStep(step.step_code)) return;
      const stepPhase = phaseForStep(step.step_code, step.display_group);
      if (!buckets[stepPhase]) buckets[stepPhase] = [];
      buckets[stepPhase].push({
        step_code: step.step_code,
        label: step.label,
        order: step.step_order ?? 0,
        phase: stepPhase,
      });
    });
    Object.values(buckets).forEach((list) => list.sort((a, b) => a.order - b.order));
    return buckets;
  }, [pipelineSteps]);

  const workflowStepOptions = useMemo(() => (
    pipelineSteps
      .filter((step) => isChecklistStep(step.step_code))
      .filter((step) => {
        if (!selectedPhase) return true;
        return phaseForStep(step.step_code, step.display_group) === selectedPhase;
      })
      .map((step) => ({
        value: step.step_code,
        label: stepFilterLabel(step, { includePhase: !selectedPhase }),
      }))
  ), [pipelineSteps, selectedPhase]);

  const selectedStepCode = singleOrEmpty(currentWorkflowStepCode);
  const selectedStep = pipelineSteps.find((step) => step.step_code === selectedStepCode);
  const phaseShortLabel = LIFECYCLE_PHASES.find((phase) => phase.code === selectedPhase)?.shortLabel;
  const pageTitle = phaseShortLabel
    ? (selectedStep ? `${phaseShortLabel} — ${selectedStep.label}` : phaseShortLabel)
    : 'Sites Klub';

  const load = ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    klubService.listAdherents({
      search,
      formule: singleOrEmpty(formule),
      dolibarr_match_status: singleOrEmpty(matchStatus),
      current_workflow_step_code: singleOrEmpty(currentWorkflowStepCode),
      lifecycle_phase: singleOrEmpty(lifecyclePhase),
      preavis: singleOrEmpty(preavis),
      mois: singleOrEmpty(mois),
      dolibarr_detected: fromDolibarr.includes('1'),
      renouvellement_60j: renouvellement60j.includes('1'),
      limit: 200,
    })
      .then((res) => {
        let rows = res.adherents ?? [];
        if (formule.length > 1) {
          rows = rows.filter((a) => formule.includes(a.formule));
        }
        if (matchStatus.length > 1) {
          rows = rows.filter((a) => matchStatus.includes(a.dolibarr_match_status));
        }
        if (currentWorkflowStepCode.length > 1) {
          rows = rows.filter((a) => currentWorkflowStepCode.some((code) => {
            const rowPhase = a.lifecycle_phase ?? phaseFromStatut(a.statut);
            if (rowPhase === PHASE_INTEGRATION && phaseForStep(code) === PHASE_INTEGRATION) {
              return !isStepDone(a.workflow_statuses?.[code]);
            }
            return (a.current_workflow_step_code ?? a.current_workflow_step?.step_code) === code;
          }));
        }
        if (lifecyclePhase.length > 1) {
          rows = rows.filter((a) => {
            const rowPhase = a.lifecycle_phase ?? phaseFromStatut(a.statut);
            return lifecyclePhase.includes(rowPhase);
          });
        }
        setAdherents(rows);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    klubService.getPipelineTemplate()
      .then((template) => {
        if (!cancelled) setPipelineTemplate(template);
      })
      .catch(() => {
        if (!cancelled) setPipelineTemplate(null);
      })
      .finally(() => {
        if (!cancelled) setCatalogReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!catalogReady) return undefined;
    const timer = setTimeout(() => load(), 300);
    return () => clearTimeout(timer);
  }, [catalogReady, search, formule, matchStatus, currentWorkflowStepCode, lifecyclePhase, fromDolibarr, renouvellement60j, preavis, mois]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasActiveFilters = Boolean(
    search || formule.length || matchStatus.length || currentWorkflowStepCode.length || mois.length,
  );

  return (
    <MasterLayout>
      <Breadcrumb
        title={pageTitle}
        actions={(
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <AppButton
              as={Link}
              to={ROUTES.klub.adherentNew}
              state={{ listReturnTo }}
              size="sm"
              variant="primary"
              icon="mdi:plus"
            >
              Nouvel site
            </AppButton>
            <AppButton
              size="sm"
              variant="outline-secondary"
              icon="mdi:arrow-left"
              onClick={() => goBackOr(navigate, dashboardBackUrl)}
            >
              Retour
            </AppButton>
          </div>
        )}
      />

      <AppFilterBar
        onReset={() => {
          setSearch('');
          setFormule([]);
          setMatchStatus([]);
          setCurrentWorkflowStepCode([]);
          setMois([]);
          setSearchParams((prev) => {
            const next = new URLSearchParams();
            const phase = prev.get('lifecycle_phase');
            const preavisParam = prev.get('preavis');
            if (phase) next.set('lifecycle_phase', phase);
            if (preavisParam) next.set('preavis', preavisParam);
            return next;
          });
        }}
        resetDisabled={!hasActiveFilters}
      >
        <AppFilterInput
          id="klub-search"
          label="Recherche"
          placeholder="Entreprise, contact, email…"
          value={search}
          onChange={setSearch}
        />
        <AppFilterSelect
          id="klub-pipeline"
          label="Étapes"
          col="col-12 col-md-6 col-lg-3"
          value={currentWorkflowStepCode}
          onChange={setCurrentWorkflowStepCode}
          multiple={false}
          options={workflowStepOptions}
        />
        <AppFilterSelect
          id="klub-formule"
          label="Formule"
          value={formule}
          onChange={setFormule}
          multiple={false}
          options={Object.entries(FORMULE_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <AppFilterSelect
          id="klub-dolibarr"
          label="Liaison Dolibarr"
          value={matchStatus}
          onChange={setMatchStatus}
          multiple={false}
          options={Object.entries(MATCH_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
        />
        {(isActifsView || isResiliationView) && (
          <AppFilterSelect
            id="klub-mois"
            label={isResiliationView ? 'Mois de résiliation' : 'Mois anniversaire'}
            value={mois}
            onChange={setMois}
            multiple={false}
            options={MONTH_OPTIONS}
          />
        )}
      </AppFilterBar>

      {error && <AppAlert variant="danger">{error}</AppAlert>}

      <KlubSiteList
        adherents={adherents}
        loading={loading}
        stepsByPhase={stepsByPhase}
        phase={selectedPhase}
        listReturnTo={listReturnTo}
        onChanged={() => load({ silent: true })}
        defaultSort={renouvellement60j.includes('1') ? { key: 'renouvellement', direction: 'asc' } : null}
      />
    </MasterLayout>
  );
};

export default KlubListingPage;
